// MEETING MODE — the arithmetic (v4.0.0, design 23/10/2026; docs/meeting-mode/SPEC.md).
//
// Pure: no DOM, no Firestore. Everything a meeting screen shows is DERIVED
// here from records Track already keeps — people, projects, tasks, leave,
// record changes, notes — and the previous meeting record. views/meeting.js
// draws it; test/meeting-model.test.mjs pins it.
//
// MONEY NEVER COMES IN. None of these functions takes a fee, an hour, a rate or
// a contact, so nothing they return — and above all `roomSnapshot`, which the
// room display reads — can carry one.

import { dayKey, addDays, weekDays, daysBetween, isSiteVisit, blockedDays } from './tasks.js';

export const NOOWNER = '_noowner';
export const WRAP = '_wrap';
export const LIVE = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued'];
// Stage order, for "up from Review".
const STAGE_ORDER = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued', 'Complete'];
const STAGE_LABEL = { 'Not Started': 'Not started', 'In Progress': 'In progress', Review: 'Review', 'Ready to Issue': 'Ready to issue', 'Part Issued': 'Part issued', Issued: 'Issued', Complete: 'Complete', 'On Hold': 'On hold', Void: 'Void' };
export const stageLabel = (s) => STAGE_LABEL[s] || s || 'Not started';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const p2 = (n) => String(n).padStart(2, '0');
const tail = (ref) => String(ref || '').split('/').pop();

/** 'YYYY-MM-DD' (or an ISO moment) → Date at local midnight, or null. */
export function dayOf(v) {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  if (String(v).length > 10) { const d = new Date(v); if (!isNaN(d)) return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  return new Date(+m[1], +m[2] - 1, +m[3]);
}
const keyOf = (v) => { const d = dayOf(v); return d ? dayKey(d) : null; };
/** 'Mon 26/10' */
export function dowDm(v) { const d = dayOf(v); return d ? `${DOW[d.getDay()]} ${p2(d.getDate())}/${p2(d.getMonth() + 1)}` : ''; }
/** '23/10' */
export function dm(v) { const d = dayOf(v); return d ? `${p2(d.getDate())}/${p2(d.getMonth() + 1)}` : ''; }
/** 'Wed 21' */
export function dowD(v) { const d = dayOf(v); return d ? `${DOW[d.getDay()]} ${d.getDate()}` : ''; }
/** 'Wed' */
export function dow(v) { const d = dayOf(v); return d ? DOW[d.getDay()] : ''; }
/** 'Monday 19/10' */
export function longDayDm(v) { const d = dayOf(v); return d ? `${DOW_LONG[d.getDay()]} ${p2(d.getDate())}/${p2(d.getMonth() + 1)}` : ''; }
/** 'Thursday 22 October' */
export function longDate(d = new Date()) { return `${DOW_LONG[d.getDay()]} ${d.getDate()} ${MON_LONG[d.getMonth()]}`; }
/** 'Monday' */
export function weekdayName(v) { const d = dayOf(v); return d ? DOW_LONG[d.getDay()] : ''; }
/** '09:15' (local) */
export function hhmm(d = new Date()) { return `${p2(d.getHours())}:${p2(d.getMinutes())}`; }
export const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';
export const initialsOf = (name) => String(name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

// ── Who is in ──────────────────────────────────────────────────────────────

/**
 * The second office, when the company has one and somebody works in it:
 * { label, ids } — else null. A person's office is `users.office`; anyone with an
 * office set that is not the first office counts as remote (ASP's sync writes
 * "India" for people the settings call "Chennai").
 */
export function remoteOffice(offices = [], users = []) {
  if (!Array.isArray(offices) || offices.length < 2) return null;
  const home = String(offices[0].label || '').toLowerCase();
  const ids = users.filter((u) => u && u.office && String(u.office).toLowerCase() !== home).map((u) => u.userId || u.id);
  return ids.length ? { label: offices[1].label, ids } : null;
}

/** Approved leave covering `day` for this email. */
export function onLeave(email, leave = [], day = dayKey()) {
  const e = String(email || '').toLowerCase();
  return leave.some((r) => r && r.status === 'approved' && String(r.userEmail || '').toLowerCase() === e
    && r.from <= day && (r.to || r.from) >= day);
}

export const ORDER_PRESETS = ['remote-first', 'most-live', 'az', 'last'];
export function presetLabel(key, remote) {
  if (key === 'remote-first') return remote ? `${remote.label} first, most live work, me last` : 'Most live work, me last';
  if (key === 'most-live') return 'Most live work first';
  if (key === 'az') return 'A to Z';
  return 'As last time';
}

/**
 * The order people are taken in. `people` [{userId,name}], ctx { live(id) →
 * number, meId, remote: {ids}|null, lastOrder: [ids] }.
 */
export function orderPeople(people = [], preset = 'remote-first', ctx = {}) {
  const live = ctx.live || (() => 0);
  const byName = (a, b) => String(a.name).localeCompare(String(b.name));
  const byLive = (a, b) => live(b.userId) - live(a.userId) || byName(a, b);
  const list = people.slice();
  if (preset === 'az') return list.sort(byName).map((p) => p.userId);
  if (preset === 'last' && Array.isArray(ctx.lastOrder) && ctx.lastOrder.length) {
    const at = (id) => { const i = ctx.lastOrder.indexOf(id); return i < 0 ? 1e6 : i; };
    return list.sort((a, b) => at(a.userId) - at(b.userId) || byLive(a, b)).map((p) => p.userId);
  }
  if (preset === 'most-live') return list.sort(byLive).map((p) => p.userId);
  // remote-first: the second office first (their day is ending), then most
  // live work, and whoever is running the meeting last.
  const remote = new Set((ctx.remote && ctx.remote.ids) || []);
  const rank = (p) => (p.userId === ctx.meId ? 2 : remote.has(p.userId) ? 0 : 1);
  return list.sort((a, b) => rank(a) - rank(b) || byLive(a, b)).map((p) => p.userId);
}

/**
 * WHO IS IN BY DEFAULT (Karan 26/10/2026: "Pam and Steve hidden by default
 * unless they have a project they're working on"). Someone whose timesheets
 * are not required (users.timesheetExempt === true) is left out of the default
 * order unless they lead a live project or have an open task. They are listed
 * under "Not in this meeting" with Add — adding is for THIS meeting only (its
 * order is what the meeting record keeps), never a setting. A room screen never
 * reaches here at all (teamFrom → people.js).
 *   people   the meeting's people ({ userId, … })
 *   users    usersById (the users docs)
 *   projects the portfolio; tasks the open tasks
 * Returns { inIds: [...], out: [people left out] }.
 */
export function meetingDefaults(people = [], { users = {}, projects = [], tasks = [] } = {}) {
  const inIds = []; const out = [];
  for (const p of people) {
    const u = users[p.userId] || {};
    const busy = projectsLedBy(projects, p.userId).length > 0
      || tasks.some((t) => isOpenTask(t) && t.assigneeId === p.userId);
    if (u.timesheetExempt === true && !busy) out.push(p); else inIds.push(p.userId);
  }
  return { inIds, out };
}

// ── A person's work ────────────────────────────────────────────────────────

export const isLive = (p) => !!p && LIVE.includes(p.status) && !p.isTest;
export const leadOf = (p) => tail(p && p.lead) || null;

/** Their live projects, soonest deadline first, no date last. */
export function projectsLedBy(projects = [], userId) {
  return projects.filter((p) => isLive(p) && leadOf(p) === userId)
    .sort((a, b) => String(keyOf(a.due) || '9999').localeCompare(String(keyOf(b.due) || '9999'))
      || String(a.number || '').localeCompare(String(b.number || '')));
}

export const isOpenTask = (t) => !!t && t.state !== 'done';
export const isLateTask = (t, today = dayKey()) => isOpenTask(t) && t.state !== 'blocked' && !!t.dueDate && t.dueDate < today;
const whenKey = (t) => t.plannedDate || t.dueDate || null;

/**
 * A person's open tasks in the meeting's groups: Overdue · This week (the next
 * seven days) · Held up (blocked, whatever its date) · Later · No date.
 * Empty groups are dropped, except that the caller may want counts.
 */
export function groupTasks(tasks = [], today = dayKey()) {
  const week = addDays(today, 6);
  const sortBy = (a, b) => String(whenKey(a) || '').localeCompare(String(whenKey(b) || '')) || String(a.title).localeCompare(String(b.title));
  const g = { overdue: [], week: [], held: [], later: [], nodate: [] };
  for (const t of tasks.filter(isOpenTask)) {
    if (t.state === 'blocked') g.held.push(t);
    else if (isLateTask(t, today)) g.overdue.push(t);
    else if (!whenKey(t)) g.nodate.push(t);
    else if (whenKey(t) <= week) g.week.push(t);
    else g.later.push(t);
  }
  return [
    { key: 'overdue', label: 'Overdue', tasks: g.overdue.sort(sortBy) },
    { key: 'week', label: 'This week', tasks: g.week.sort(sortBy) },
    { key: 'held', label: 'Held up', tasks: g.held.sort(sortBy) },
    { key: 'later', label: 'Later', tasks: g.later.sort(sortBy) },
    { key: 'nodate', label: 'No date', tasks: g.nodate },
  ].filter((x) => x.tasks.length);
}

/** The right-hand word on a task row: "2 days" late, "Today", "Fri 23/10". */
export function taskWhen(t, today = dayKey()) {
  if (!t) return '';
  if (t.state === 'blocked') return '';
  if (isLateTask(t, today)) { const n = daysBetween(t.dueDate, today); return `${n} day${n === 1 ? '' : 's'}`; }
  const k = whenKey(t);
  if (!k) return '';
  if (k === today) return 'Today';
  return dowDm(k);
}

/** "waiting on the client's GI report, 9 days" */
export function heldText(t, today = dayKey()) {
  const b = t && t.blocker;
  if (!b) return 'held up';
  const who = String(b.waitingOnName || '').trim() || 'someone';
  const n = blockedDays(t, today);
  return `waiting on ${who}${n ? `, ${n} day${n === 1 ? '' : 's'}` : ''}`;
}

/** Leave, a site visit, or the office, for each weekday of the week holding `today`. */
export function weekStrip(person, { leave = [], tasks = [], projectsById = {}, today = dayKey() } = {}) {
  return weekDays(today).map((d) => {
    const base = { d, dow: dow(d), date: dayOf(d).getDate(), today: d === today };
    if (person && onLeave(person.email, leave, d)) return { ...base, kind: 'leave', label: 'Leave' };
    // A site visit counts on the day it was planned or due — or, once done, the day it was done.
    const site = tasks.find((t) => t && t.assigneeId === (person && person.userId) && isSiteVisit(t)
      && (whenKey(t) || keyOf(t.completedAt)) === d);
    if (site) {
      const no = site.projectId && projectsById[site.projectId] ? projectsById[site.projectId].number : '';
      return { ...base, kind: 'site', label: no ? `Site · ${no}` : 'Site', no: no || '' };
    }
    return { ...base, kind: 'office', label: 'Office' };
  });
}

// ── What moved since the last meeting ──────────────────────────────────────

/** The changes, finished tasks and notes on one project since `since` (ISO). */
export function projectSince(p, { changes = [], tasksDone = [], notes = [], tasksMade = [], since = '' } = {}) {
  const rec = `projects/${p.id}`;
  const ch = changes.filter((c) => c && c.record === rec && String(c.at || '') >= since);
  const done = tasksDone.filter((t) => t && t.projectId === p.id && String(t.completedAt || '') >= since);
  const nt = notes.filter((n) => n && (n.projectId === p.id || (p.number && String(n.projectNo) === String(p.number))) && String(n.createdAt || '') >= since);
  const made = tasksMade.filter((t) => t && t.projectId === p.id && String(t.createdAt || '') >= since);
  return { changes: ch, done, notes: nt, made };
}

/** The line under the stage pill: what moved, or how long it has been quiet. */
export function movedLine(p, s, { emails = 0, today = new Date() } = {}) {
  const stage = s.changes.filter((c) => c.field === 'status').sort((a, b) => String(b.at).localeCompare(String(a.at)))[0];
  if (stage && stage.from && STAGE_ORDER.indexOf(stage.to) > STAGE_ORDER.indexOf(stage.from)) {
    return { text: `up from ${stageLabel(stage.from)} · ${dow(stage.at)}`, tone: 'moved' };
  }
  const parts = [];
  const visit = s.done.find(isSiteVisit);
  if (visit) parts.push(`site visit ${dow(visit.completedAt)}`);
  if (emails) parts.push(`${emails} email${emails === 1 ? '' : 's'}`);
  if (!parts.length && s.made.length) parts.push(`new task · ${dow(s.made[0].createdAt)}`);
  if (!parts.length && s.notes.length) parts.push(`${s.notes.length} note${s.notes.length === 1 ? '' : 's'}`);
  if (!parts.length && s.done.length) parts.push(`${s.done.length} task${s.done.length === 1 ? '' : 's'} done`);
  if (parts.length) return { text: parts.join(' · '), tone: 'moved' };
  const last = dayOf(p.lastActivityAt || p.updatedAt || p.statusUpdatedAt);
  if (last) {
    const n = Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - last) / 86400000);
    if (n >= 7) return { text: `quiet for ${n} days`, tone: 'quiet' };
  }
  return { text: '', tone: '' };
}

/** The expanded row's dated history, newest first. `name(email)` → first name. */
export function historyLines(s, { name = (e) => firstName(String(e || '').split('@')[0]) } = {}) {
  const rows = [];
  for (const c of s.changes) {
    const who = c.byName ? firstName(c.byName) : name(c.by);
    if (c.field === 'status') rows.push({ at: c.at, text: `Moved to ${stageLabel(c.to)}${who ? ` (${who})` : ''}` });
    else if (c.field === 'due') rows.push({ at: c.at, text: c.to ? `New date ${dm(c.to)}${c.from ? `, was ${dm(c.from)}` : ''}` : 'Date taken off' });
    else if (c.field === 'lead') rows.push({ at: c.at, text: `Lead: ${c.to || 'nobody'}` });
    else if (!c.money) rows.push({ at: c.at, text: `${c.label || c.field} changed` });
  }
  for (const t of s.done) rows.push({ at: t.completedAt, text: `${firstName(t.assigneeName || '') || name(t.assigneeEmail) || 'Someone'} finished: ${t.title}` });
  for (const n of s.notes) rows.push({ at: n.createdAt, text: `${firstName(n.userName) || name(n.userEmail)}: ${String(n.text || '').slice(0, 140)}` });
  return rows.filter((r) => r.at).sort((a, b) => String(b.at).localeCompare(String(a.at))).map((r) => ({ ...r, when: dowD(r.at) }));
}

/** How much changed on someone's work since the last meeting (Start's MOVED). */
export function movedCount(userId, { projects = [], changes = [], tasksDone = [], since = '' } = {}) {
  const led = new Set(projects.filter((p) => leadOf(p) === userId).map((p) => `projects/${p.id}`));
  const ch = changes.filter((c) => led.has(c.record) && String(c.at || '') >= since && !c.money).length;
  const td = tasksDone.filter((t) => t.assigneeId === userId && String(t.completedAt || '') >= since).length;
  return ch + td;
}

/**
 * "From last time · Monday 19/10 · 5 of 7 done". `open` = the team's open tasks;
 * an action id that is not among them is finished (tasks are never deleted).
 */
export function fromLastTime(last, open = [], today = dayKey()) {
  if (!last || !Array.isArray(last.actionTaskIds) || !last.actionTaskIds.length) return null;
  const byId = Object.fromEntries(open.map((t) => [t.id, t]));
  const still = last.actionTaskIds.map((id) => byId[id]).filter(Boolean);
  const late = (t) => (t.dueDate && t.dueDate < today ? daysBetween(t.dueDate, today) : 0);
  return {
    when: last.startedAt,
    total: last.actionTaskIds.length,
    done: last.actionTaskIds.length - still.length,
    open: still.map((t) => ({ ...t, lateDays: late(t) })).sort((a, b) => b.lateDays - a.lateDays),
  };
}

// ── No owner ───────────────────────────────────────────────────────────────

export const unownedTasks = (tasks = []) => tasks.filter((t) => isOpenTask(t) && !t.assigneeId);
export const unledProjects = (projects = []) => projects.filter((p) => isLive(p) && !leadOf(p));

/**
 * Who could take it: the person with the shortest list among the available
 * people of the team's most common role ("Apam has the shortest list of the
 * engineers"). counts: { userId: openTasks }. Returns null when nobody is free.
 */
export function suggestOwner(people = [], counts = {}, away = new Set()) {
  const free = people.filter((p) => !away.has(p.userId));
  if (!free.length) return null;
  const tally = {};
  for (const p of people) { const r = String(p.role || '').trim(); if (r) tally[r] = (tally[r] || 0) + 1; }
  const top = Object.entries(tally).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  const role = top && top[1] > 1 ? top[0] : '';
  const pool = role ? free.filter((p) => p.role === role) : free;
  const list = (pool.length ? pool : free).slice()
    .sort((a, b) => (counts[a.userId] || 0) - (counts[b.userId] || 0) || String(a.name).localeCompare(String(b.name)));
  const who = list[0];
  const plural = role ? `${role.toLowerCase()}${/s$/i.test(role) ? '' : 's'}` : '';
  return { userId: who.userId, text: `${firstName(who.name)} has the shortest list${plural && pool.length ? ` of the ${plural}` : ''}` };
}

// ── The action bar ─────────────────────────────────────────────────────────

/** The chips a parsed line resolves to: person, project, date (design p5). */
export function actionChips(parsed, { person = null, projectsById = {} } = {}) {
  if (!parsed || !String(parsed.raw || '').trim()) return [];
  const chips = [];
  const owner = parsed.owner ? { name: parsed.owner.name, initials: initialsOf(parsed.owner.name) } : person;
  if (owner) chips.push({ kind: 'person', initials: owner.initials || initialsOf(owner.name), label: firstName(owner.name) });
  if (parsed.project) {
    const p = projectsById[parsed.project.id] || parsed.project;
    chips.push({ kind: 'project', no: p.number || '', label: p.clientName || parsed.project.name || '' });
  } else if (parsed.ambiguous && parsed.ambiguous.kind === 'project') {
    chips.push({ kind: 'ask', label: `${parsed.ambiguous.matches.length} projects match — type the number` });
  }
  if (parsed.dueDate) chips.push({ kind: 'date', label: dowDm(parsed.dueDate) });
  return chips;
}

// ── The room screen ────────────────────────────────────────────────────────

/**
 * What the room display shows for the current screen — and nothing else. Its
 * inputs are names, numbers, stages and dates; it has no way to be handed a fee.
 * view: { screen: 'person'|'noowner'|'wrap', person: {name, role}, projects:
 * [{number, clientName, title, status, moved, due, late}], openTasks, overdue,
 * week: weekStrip(), strip: [{initials, name, state}], next, clocks }
 */
export function roomSnapshot(view = {}) {
  const s = (v, n = 80) => String(v == null ? '' : v).slice(0, n);
  const person = view.person || null;
  return {
    screen: ['person', 'noowner', 'wrap'].includes(view.screen) ? view.screen : 'person',
    name: s(person && person.name),
    initials: s(person && initialsOf(person.name), 3),
    sub: s(view.sub, 120),
    week: (view.week || []).slice(0, 5).map((d) => ({ d: s(d.d, 10), label: s(d.label, 24), kind: s(d.kind, 8), today: !!d.today })),
    projects: (view.projects || []).slice(0, 8).map((p) => ({
      no: s(p.number, 12), client: s(p.clientName, 60), title: s(p.title, 80),
      stage: s(stageLabel(p.status), 20), note: s(p.moved, 60), due: s(p.due ? dm(p.due) : '', 5), late: !!p.late,
    })),
    openTasks: Number(view.openTasks) || 0,
    overdue: Number(view.overdue) || 0,
    strip: (view.strip || []).slice(0, 30).map((x) => ({ i: s(x.initials, 3), n: s(firstName(x.name), 24), state: s(x.state, 8) })),
    next: s(view.next, 60),
    clocks: (view.clocks || []).slice(0, 3).map((c) => ({ label: s(c.label, 24), tz: s(c.tz, 40) })),
  };
}

/** Seconds left on the ring (negative once over). */
export function ringLeft(personStartedAt, minutesEach, now = Date.now()) {
  const start = Date.parse(personStartedAt || '');
  const total = Math.max(1, Number(minutesEach) || 3) * 60;
  if (isNaN(start)) return total;
  return Math.round(total - (now - start) / 1000);
}
export function ringText(left) {
  const a = Math.abs(left);
  return `${left < 0 ? '+' : ''}${Math.floor(a / 60)}:${p2(a % 60)}`;
}

/** A meeting is live for followers and the room only while its runner is with it. */
export const STALE_MS = 15 * 60000;
export function meetingLive(m, now = Date.now()) {
  if (!m || m.state !== 'running') return false;
  const at = Date.parse(m.updatedAt || m.startedAt || '');
  return !isNaN(at) && now - at < STALE_MS;
}

// ── Keeping a running meeting current (v4.6.3, Karan 26/10/2026) ───────────
// "When I change the status etc during a meeting, it doesn't update within the
// meeting." The meeting holds the Projects screen's objects; these fold what
// changed — from the meeting's own write, another tab (a live listener), or the
// runner's record as a follower reads it — into those SAME objects, so every
// view (the rows, the room snapshot, the Projects list behind) reads one truth.

// A stored value as something comparable: a Firestore Timestamp compares by
// its moment, everything else by its JSON.
const norm = (v) => (v && typeof v.toDate === 'function' ? `ts:${v.toDate().toISOString()}` : JSON.stringify(v === undefined ? null : v));
const same = (a, b) => norm(a) === norm(b);
const PROJECT_FIELDS = { status: 'status', due: 'due', lead: 'lead' };

/**
 * Fold live project rows into the meeting's list IN PLACE (byId: id → object).
 * A project the meeting has not seen is added. Returns { changed, moves }: moves
 * are the stage / date / lead changes seen, shaped like record changes
 * ({ record, field, from, to, at, by, byName }) so "moved" and the history
 * lines can say what happened. Only the fields a row carries are compared.
 */
export function mergeProjects(projects, byId, rows = []) {
  let changed = false;
  const moves = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || !r.id) continue;
    const p = byId[r.id];
    if (!p) {
      if (r.isTest) continue;
      const n = { ...r }; projects.push(n); byId[n.id] = n; changed = true;
      continue;
    }
    const diff = Object.keys(r).some((k) => !same(p[k], r[k]));
    if (!diff) continue;
    const at = String(r.updatedAt || r.statusUpdatedAt || new Date().toISOString());
    for (const f of Object.keys(PROJECT_FIELDS)) {
      if (f in r && !same(p[f], r[f])) {
        moves.push({ record: `projects/${p.id}`, field: f, from: p[f] == null ? null : p[f], to: r[f] == null ? null : r[f],
          at: f === 'status' && r.statusUpdatedAt ? String(r.statusUpdatedAt) : at, by: r.updatedBy || '', byName: '' });
      }
    }
    Object.assign(p, r);
    changed = true;
  }
  return { changed, moves };
}

const TASK_FIELDS = ['state', 'assigneeId', 'assigneeEmail', 'checkerId', 'dueDate', 'plannedDate', 'title', 'projectId', 'blocker', 'completedAt'];
/**
 * Fold the live open-task list into the meeting's tasks IN PLACE. A task gone
 * from the open list is done (or deleted) and leaves; one made in this meeting a
 * moment ago (`_justNow`, or a local- id) is kept until the list catches up.
 */
export function mergeTasks(tasks, rows) {
  if (!Array.isArray(rows)) return false;
  const live = new Map(rows.filter((t) => t && t.id).map((t) => [t.id, t]));
  let changed = false;
  for (let i = tasks.length - 1; i >= 0; i--) {
    const t = tasks[i]; const r = live.get(t.id);
    if (r) {
      if (TASK_FIELDS.some((k) => !same(t[k], r[k]))) { Object.assign(t, r); changed = true; }
      live.delete(t.id);
    } else if (isOpenTask(t) && !t._justNow && !String(t.id || '').startsWith('local-')) {
      tasks.splice(i, 1); changed = true;
    }
  }
  for (const r of live.values()) { tasks.push({ ...r }); changed = true; }
  return changed;
}

/**
 * A follower's copy: apply the runner's recorded changes (meetings/{id}.changes)
 * to the follower's own projects, once each. `applied` is a Set the caller
 * keeps; `leadPath(userId)` makes a lead reference. A change whose value the
 * project already has (the live listener got there first) is marked applied and
 * not repeated. Returns the record-change shaped moves it made.
 */
export function applyRecordedChanges(byId, changes = [], applied = new Set(), leadPath = (id) => (id ? `users/${id}` : null)) {
  const moves = [];
  for (const c of Array.isArray(changes) ? changes : []) {
    if (!c || !c.projectId) continue;
    const key = `${c.projectId}|${c.kind}|${c.at}`;
    if (applied.has(key)) continue;
    applied.add(key);
    const p = byId[c.projectId]; if (!p) continue;
    let field = null; let to;
    if (c.kind === 'stage') { field = 'status'; to = c.to; }
    else if (c.kind === 'due') { field = 'due'; to = c.to || null; }
    else if (c.kind === 'lead' && 'toId' in c) { field = 'lead'; to = leadPath(c.toId); }
    if (!field || same(p[field], to)) continue;
    moves.push({ record: `projects/${p.id}`, field, from: p[field] == null ? null : p[field], to: field === 'lead' ? (c.to || null) : to, at: c.at, by: '', byName: c.byName || '' });
    p[field] = to;
  }
  return moves;
}

/**
 * The selected row follows its PROJECT, not its position: a new date re-sorts
 * the list, and a live change can add or drop rows. When the project has left
 * the list (moved past Part issued), the row now at its place is selected.
 */
export function pinSelection(list = [], selPid = null, sel = 0) {
  const n = list.length;
  if (!n) return { sel: 0, selPid: null };
  const i = selPid ? list.findIndex((p) => p && p.id === selPid) : -1;
  const at = i >= 0 ? i : Math.max(0, Math.min(n - 1, Number(sel) || 0));
  return { sel: at, selPid: list[at].id };
}
